import type { ReactNode } from 'react';
import { Circle, Shuffle, Trophy, Handshake, Users, Crown, Skull, Sword, Dices, Feather, Repeat, File, EyeOff } from 'lucide-react';
import { isDrawnOpening, type OpeningMode } from '@draughts/shared';

/**
 * Leading glyphs for the direct-challenge options (colour, game type, mode,
 * opening, legs). Shared single source of truth so the challenge dropdowns
 * and the read-only summary popups (OutgoingChallengeModal, incoming banner)
 * render the same icon language. Variant and cadence have their own shared
 * helpers (variantPill / CADENCE_ICONS).
 *
 * Every glyph is aria-hidden — the accompanying text label carries the meaning.
 */

/** White / black / random-shuffle glyph for a colour option. */
export const colorLeading = (c: string, size = 14): ReactNode => {
  if (c === 'white') return <Circle size={size} fill="#ffffff" stroke="#4A4A4A" strokeWidth={1.5} aria-hidden="true" />;
  if (c === 'black') return <Circle size={size} fill="#1B1F20" stroke="#1B1F20" aria-hidden="true" />;
  return <Shuffle size={size} aria-hidden="true" />;
};

/**
 * Game-type glyph: trophy (solo valida) / handshake (solo amichevole) /
 * people (qualsiasi — open to everyone, counts when it can).
 */
export const gameTypeLeading = (v: string, size = 14): ReactNode => {
  if (v === 'rated') return <Trophy size={size} aria-hidden="true" />;
  if (v === 'casual') return <Handshake size={size} aria-hidden="true" />;
  return <Users size={size} aria-hidden="true" />;
};

/**
 * Game-mode glyph: crown (breakthrough) / skull (antidraughts) / crossed-out eye
 * (blind) / single sword (standard).
 *
 * Standard used to return null, on the grounds that the ordinary game needs no
 * marking. In a menu it does: VariantSelect renders the leading element inline
 * with no fixed slot, so the one option without a glyph started further left than
 * the other three.
 *
 * The sword is `Sword`, NEVER `Swords`: the crossed pair means "arena" everywhere
 * else in the app (sidebar, arena pages, tournament format badge, the game rows'
 * origin badge), and this glyph must not borrow that meaning. The two never appear
 * side by side — this branch is rendered only by the two mode menus (direct
 * challenge and AI challenge), which carry no arena glyph — so one blade vs two
 * stays legible even at 14px.
 *
 * It cannot leak into a game row or a badge: every caller outside the two menus
 * renders a mode chip only for a NON-standard mode.
 */
export const gameModeLeading = (m: string, size = 14): ReactNode => {
  // breakthrough = first king wins; antidraughts = loser wins ("suicide");
  // blind = same rules, invisible pieces; normal = standard opposition game.
  if (m === 'breakthrough') return <Crown size={size} aria-hidden="true" />;
  if (m === 'antidraughts') return <Skull size={size} aria-hidden="true" />;
  if (m === 'blind') return <EyeOff size={size} aria-hidden="true" />;
  return <Sword size={size} aria-hidden="true" />;
};

/**
 * Dice (drawn, whatever its length) / feather (free) glyph for the opening option.
 * Typed as OpeningMode so a new drawn mode can never silently fall through to the
 * free-opening glyph the way a plain `=== 'drawn'` string compare would.
 */
export const openingLeading = (o: OpeningMode, size = 14): ReactNode =>
  isDrawnOpening(o) ? <Dices size={size} aria-hidden="true" /> : <Feather size={size} aria-hidden="true" />;

/** Repeat (andata e ritorno) / single sheet (solo andata) glyph for the legs option. */
export const legsLeading = (legs: string | number, size = 14): ReactNode =>
  Number(legs) > 1 ? <Repeat size={size} aria-hidden="true" /> : <File size={size} aria-hidden="true" />;
